import { Element } from 'model/Form';
import { useMemo, useState } from 'react';

interface UseFormsFieldsSharedLogicProps {
  element: Element;
  value?: string;
}

const useFormsFieldsSharedLogic = ({ element, value = '' }: UseFormsFieldsSharedLogicProps) => {
  const [formStartEventExistInDataLayer, setFormStartEventExistInDataLayer] = useState(false);

  const {
    displayType,
    name,
    htmlName,
    id,
    instructions,
    description,
    validations,
    optionList,
    conditional,
    relatesTo,
    isRequired,
    minValue,
    maxValue,
    minNumber,
    maxNumber,
    requiredErrorMessage,
    errorMessages,
    inputType,
    options,
    formattedFields,
  } = useMemo(() => {
    const displayType = element.displayType ?? '';
    const name = element.name ?? '';
    const htmlName = element.htmlName;
    const id = element.id;
    const instructions = element.instructions;
    const description = element.description;
    const validations = element.validations;
    const optionList = element.optionList;
    const conditional = element.conditionalField || false;
    const relatesTo = element.relatesTo;

    const isRequired = !!validations?.some(
      ({ condition }) => condition.type === 'IsRequiredCondition'
    );

    const minValue = validations?.find(({ condition }) => condition.type === 'TextLengthCondition')
      ?.condition?.minimum;

    const maxValue = validations?.find(({ condition }) => condition.type === 'TextLengthCondition')
      ?.condition?.maximum;

    const minNumber = validations?.find(
      ({ condition }) => condition.type === 'MaxNumberValueCondition'
    )?.condition?.minimum;

    const maxNumber = validations?.find(
      ({ condition }) => condition.type === 'MaxNumberValueCondition'
    )?.condition?.maximum;

    const requiredErrorMessage = validations?.find(
      ({ condition }) => condition.type === 'IsRequiredCondition'
    )?.message;

    const errorMessages = {
      valueMissing: validations?.find(({ condition }) => condition.type === 'IsRequiredCondition')
        ?.message,
      typeMismatch: validations?.find(
        ({ condition }) => condition.type === 'IsEmailAddressCondition'
      )?.message,
      tooLong: validations?.find(({ condition }) => condition.type === 'TextLengthCondition')
        ?.message,
      tooShort: validations?.find(({ condition }) => condition.type === 'TextLengthCondition')
        ?.message,
      rangeOverflow: validations?.find(
        ({ condition }) => condition.type === 'MaxNumberValueCondition'
      )?.message,
      rangeUnderflow: validations?.find(
        ({ condition }) => condition.type === 'MaxNumberValueCondition'
      )?.message,
    };

    const inputType = (() => {
      switch (htmlName) {
        case 'emailAddress':
          return 'email';
        case 'singleCheckbox':
          return 'checkbox';
        case 'number':
          return 'number';
        default:
          return 'text';
      }
    })();

    const options = (() => {
      const optionListElements = optionList?.elements;

      if (optionListElements) {
        return {
          value: optionListElements.map((opt) => ({
            ...opt,
            name: opt.value, // Transform `name` based on `value`
          })),
        };
      } else {
        return {};
      }
    })();

    const formattedFields = {
      label: {
        value: isRequired ? `${name}*` : name,
      },
      placeholder: {
        value: '',
      },
      id,
      value: {
        value,
      },
      errorMessages,
      name: {
        value: htmlName,
      },
      hint: {
        value: '',
      },
      tooltip: {
        value: '',
      },
      options,
      required: {
        value: isRequired,
      },
      type: {
        value: inputType,
      },
      min: {
        value: minValue,
      },
      max: {
        value: maxValue,
      },
      maxNumberValue: {
        value: maxNumber,
      },
      minNumberValue: {
        value: minNumber,
      },
      conditionalField: {
        value: conditional,
      },
      relatesTo: {
        value: relatesTo,
      },
    };

    return {
      displayType,
      name,
      htmlName,
      id,
      instructions,
      description,
      validations,
      optionList,
      conditional,
      relatesTo,
      isRequired,
      minValue,
      maxValue,
      minNumber,
      maxNumber,
      requiredErrorMessage,
      errorMessages,
      inputType,
      options,
      formattedFields,
    };
  }, [element, value]);
  return {
    formStartEventExistInDataLayer,
    setFormStartEventExistInDataLayer,
    displayType,
    name,
    htmlName,
    id,
    instructions,
    description,
    validations,
    optionList,
    conditional,
    relatesTo,
    formattedFields,
    isRequired,
    minValue,
    maxValue,
    minNumber,
    maxNumber,
    requiredErrorMessage,
    errorMessages,
    inputType,
    options,
  };
};

export type FormattedFields = ReturnType<typeof useFormsFieldsSharedLogic>['formattedFields'];
export default useFormsFieldsSharedLogic;
